import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage: string;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, errorMessage: error.message };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('App error boundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-background-50 text-foreground-900">
          <div className="max-w-lg w-full text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-2xl text-primary-600" />
            </div>
            <h1 className="font-heading text-2xl mb-3">Something went wrong</h1>
            <p className="text-foreground-600 font-body text-sm mb-6 leading-relaxed">
              The page failed to load. This is usually a temporary issue. Please try refreshing, or contact support if it persists.
            </p>
            {this.state.errorMessage && (
              <div className="bg-background-100 border border-background-200 rounded-lg p-4 mb-6 text-left overflow-auto">
                <p className="text-foreground-500 font-mono text-xs break-all">
                  {this.state.errorMessage}
                </p>
              </div>
            )}
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-6 py-2.5 rounded-md text-sm font-body font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line" />
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}